import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "react-jss";
import { useParams } from "react-router";
import { Milliseconds } from "../../../common/enum";
import { FormatDate } from "../../../common/enum/dateTime/FormatDate";
import { IOrganismScreen, IOrganismScreenRow } from "../../../models/management/organismScreen/IOrganismScreen";
import { fetchManagementOrganismScreenAction } from "../../../redux/actions/management/organismScreen/organismScreen";
import { convertToLocal } from "../../../utils";
import { DigitalTimer } from "../../common/digitalTimer/digitalTimer";
import { EnvironmentDescription } from "../../common/environment/environment";
import { NavHeaderComponent } from "../../common/navHeader/navHeader";
import { ManagementOrganismScreenContainerStyle } from "./containerStyle.jss";

export const ManagementOrganismScreenContainer = () => {
    const { t } = useTranslation("common", { keyPrefix: "MANAGEMENT.ORGANISM_SCREEN" });
    const { organization } = useParams();
    const theme = useTheme();
    const style = ManagementOrganismScreenContainerStyle({ theme });
    const [organismScreen, setOrganismScreen] = useState<IOrganismScreen | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const result = await fetchManagementOrganismScreenAction(organization);
                setOrganismScreen(result);
            } catch (error) {
                console.error(error);
            }
        };
        fetchData();
        const intervalId = setInterval(fetchData, Milliseconds.TWO_MIN);
        return () => clearInterval(intervalId);
    }, [organization]);

    const RenderRows = useMemo(() => {
        let lastRequestId: number | null = null;
        let alternate = false;

        return organismScreen?.map((row: IOrganismScreenRow) => {
            if (lastRequestId !== null && lastRequestId !== row.requestId) {
                alternate = !alternate;
            }
            lastRequestId = row.requestId;

            return (
                <div
                    key={`${row.requestId}-${row.organismCode}-${row.expedient}`}
                    className={`${style.row} ${alternate ? style.rowAlternate : ""}`}
                >
                    <div className={style.cell}>{row.transportUnitNumber}</div>
                    <div className={style.cell}>{row.expedient}</div>
                    <div className={style.cell}>{row.organismDescription}</div>
                    <div className={style.cell}>{row.dockDescription}</div>
                    <div className={style.cell}>{row.inspectorArrivedOn ? convertToLocal(row.inspectorArrivedOn, FormatDate.HOUR_MINUTE) : "-"}</div>
                    <div className={style.cell}>{row.dockAssignmentOn ? convertToLocal(row.dockAssignmentOn, FormatDate.HOUR_MINUTE) : "-"}</div>
                </div>
            );
        });
    }, [organismScreen, style]);

    return (
        <div className={style.generalContainer}>
            <NavHeaderComponent
                title={t("TITLE")}
                endAction={
                    <>
                        <EnvironmentDescription />
                        <DigitalTimer className={style.timer} />
                    </>
                }
                environmentBackground
            />
            <div className={style.header}>
                <div className={style.cell}>{t("TRANSPORT_UNIT_NUMBER")}</div>
                <div className={style.cell}>{t("EXPEDIENT")}</div>
                <div className={style.cell}>{t("ORGANISM")}</div>
                <div className={style.cell}>{t("DOCK")}</div>
                <div className={style.cell}>{t("INSPECTOR")}</div>
                <div className={style.cell}>{t("DOCK_ASSIGNMENT_ON")}</div>
            </div>
            {organismScreen && organismScreen.length > 0 ? (
                <div className={style.gridContent}>{RenderRows}</div>
            ) : (
                <div className={style.emptyState}>{t("EMPTY_STATE")}</div>
            )}
        </div>
    );
};
